'use client';

import type { SocialLink } from '@/components/social';

import { twMerge } from 'tailwind-merge';
import { CONFIG } from '@/global-config';
import React, { useState, Fragment } from 'react';
import { m, AnimatePresence } from 'framer-motion';

import { useTranslate } from '@/core/locales';
import { TRANSLATION_NAMESPACES } from '@/core/constants';

import { Logo } from '@/components/logo';
import Social from '@/components/social';
import { Iconify } from '@/components/iconify';
import { Box, Link, Grid, Image, Divider, Container, Typography } from '@/components/ui';

import { RouterLink } from '@/routes/components';

export interface FooterProps extends React.HTMLAttributes<HTMLElement> {
  socialLinksData?: SocialLink[];
  footerNavigationData?: any[];
  className?: string;
}

function SectionContent({ list, itemsPerColumn }: { list: any; itemsPerColumn: number }) {
  if (typeof list.columns === 'number' && list.columns > 1) {
    return (
      <div className="flex flex-col gap-6 lg:flex-row 2xl:gap-[74px]">
        {Array.from({ length: list.columns }).map((_, columnIndex) => {
          const start = columnIndex * itemsPerColumn;
          const end = start + itemsPerColumn;
          const columnLinks = list.children.slice(start, end);

          return (
            <div key={columnIndex} className="flex flex-col gap-6">
              {columnLinks.map((link: any) =>
                link?.href ? (
                  <Link
                    key={link.name}
                    component={RouterLink}
                    href={link.href}
                    color="inherit"
                    variant="body2"
                    className="font-normal"
                  >
                    {link.name}
                  </Link>
                ) : (
                  <Typography key={link.name} variant="body2" className="font-normal">
                    {link.name}
                  </Typography>
                )
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {list.children.map((link: any) =>
        link?.href ? (
          <Link
            key={link.name}
            component={RouterLink}
            href={link.href}
            color="inherit"
            variant="body2"
            className="font-normal"
          >
            {link.name}
          </Link>
        ) : (
          <Typography key={link.name} variant="body2" className="font-normal">
            {link.name}
          </Typography>
        )
      )}
    </div>
  );
}

export function Footer({
  socialLinksData = [],
  footerNavigationData = [],
  className,
  ...other
}: FooterProps) {
  const { t } = useTranslate(TRANSLATION_NAMESPACES.FOOTER);
  const [openIndexes, setOpenIndexes] = useState<Record<number, boolean>>({});

  const toggle = (index: number) => {
    setOpenIndexes((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const footerNavigationWithContacts = [
    ...footerNavigationData,
    {
      headline: t('footer.sections.contacts'),
      columns: 1,
      children: [
        { name: t('footer.contacts.entrepreneur'), href: null },
        { name: t('footer.contacts.taxId'), href: null },
        { name: t('footer.contacts.businessId'), href: null },
        { name: t('footer.contacts.email'), href: t('footer.contacts.emailLink') },
      ],
    },
  ];

  return (
    <Box component="footer" className={twMerge('relative bg-light-gray/56', className)} {...other}>
      <Container className="lg:pt-10 pb-[26px] lg:py-10">
        <Grid
          container
          className="lg:mt-3 flex-col-reverse xl:flex-row lg:flex-nowrap justify-between border-t pt-12 lg:pt-16 border-black/20 gap-9 md:gap-4 2xl:gap-12"
          spacing={16}
        >
          <Grid item className="flex-grow w-full">
            <Grid
              container
              className="flex justify-between w-full flex-col lg:gap-4 lg:flex-row"
              spacing={0}
              wrap="nowrap"
            >
              {footerNavigationWithContacts?.map((list, index) => {
                const isOpen = openIndexes[index];
                const itemsPerColumn = Math.ceil(list.children?.length / list.columns);

                return (
                  <Fragment key={list.headline}>
                    <Grid
                      item
                      className={twMerge(
                        'flex flex-col lg:gap-3 lg:items-start lg:px-0 py-[18px] lg:py-0 border-t border-black/20 lg:border-t-0',
                        index === footerNavigationWithContacts.length - 1 &&
                          'border-b lg:border-b-0'
                      )}
                    >
                      {/* Section header */}
                      <Box
                        className="flex items-center justify-between cursor-pointer lg:cursor-default lg:pointer-events-none lg:mb-3"
                        onClick={() => toggle(index)}
                      >
                        <Typography
                          component="h6"
                          variant="h6"
                          className="text-primary-dark uppercase"
                        >
                          {list.headline}
                        </Typography>

                        {/* Chevron only visible on small screens */}
                        <Iconify
                          icon={isOpen ? 'lucide:chevron-up' : 'lucide:chevron-down'}
                          className="lg:hidden"
                          width={20}
                        />
                      </Box>

                      {/* Animated content for small screens */}
                      <AnimatePresence initial={false}>
                        {isOpen && (
                          <m.div
                            key="section-mobile"
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3, ease: 'easeInOut' }}
                            className="overflow-hidden lg:hidden w-full mt-2"
                          >
                            <SectionContent list={list} itemsPerColumn={itemsPerColumn} />
                          </m.div>
                        )}
                      </AnimatePresence>

                      {/* Always visible on large screens */}
                      <div className="hidden lg:block w-full">
                        <SectionContent list={list} itemsPerColumn={itemsPerColumn} />
                      </div>
                    </Grid>

                    {index < footerNavigationWithContacts.length - 1 && (
                      <Divider orientation="vertical" className="hidden lg:block h-auto" />
                    )}
                  </Fragment>
                );
              })}
            </Grid>
          </Grid>

          <Grid item className="w-full lg:max-w-[261px] flex-shrink-0 flex flex-col gap-[18px]">
            <Logo />
            <Box className="flex divide-x divide-black/20">
              <Social links={socialLinksData} />
            </Box>
          </Grid>
        </Grid>

        <Box className="flex items-center justify-between flex-col lg:flex-row gap-3 lg:gap-0 pt-[36px] lg:pt-5 pb-4 lg:pb-0">
          <Box className="flex items-center gap-3">
            <Box className="w-10 h-10">
              <Image
                src={`${CONFIG.assetsDir}/logo/logo-black.svg`}
                alt="Sellersos Logo"
                objectFit="fill"
                className="w-full h-full object-contain"
                loading="eager"
                width={34}
                height={38}
              />
            </Box>
            <Typography variant="body2">
              {t('footer.copyright', { year: new Date().getFullYear() })}
            </Typography>
          </Box>
          <Box className="hidden xl:flex">
            <Box className="w-14 h-14">
              <Image
                src={`${CONFIG.assetsDir}/assets/images/footer/wb-icon.webp`}
                alt="WB Icon"
                objectFit="fill"
                className="w-full h-full object-contain"
                loading="eager"
                width={56}
                height={56}
              />
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

export function SimpleFooter({ className, ...other }: FooterProps) {
  const { t } = useTranslate(TRANSLATION_NAMESPACES.FOOTER);

  return (
    <Box
      component="footer"
      className={twMerge('relative bg-background py-5 text-center', className)}
      {...other}
    >
      <Container>
        <Box className="flex items-center gap-3 pt-[36px] lg:pt-5 pb-4 lg:pb-0">
          <Box className="w-10 h-10">
            <Image
              src={`${CONFIG.assetsDir}/logo/logo-black.svg`}
              alt="Sellersos Logo"
              objectFit="fill"
              className="w-full h-full object-contain"
              loading="eager"
              width={34}
              height={38}
            />
          </Box>
          <Typography variant="body2">
            {t('footer.copyright', { year: new Date().getFullYear() })}
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}
